/**
 * Cbullshitcoding Component
 * Displays a rolling log of "technical" messages with a typewriter effect.
 * Lines are added character by character. When the 10th line is added, the first line is removed.
 * Supports loading content from a file path or receiving strings directly.
 */

'use client';

import './Cbullshitcoding.css';
import type { ReactElement } from 'react';
import { Temper, TextAlign, WritingChunks } from '@/types/frontend-types';
import { CbullshitcodingViewport } from './CbullshitcodingViewport';
import { getCenteredBlockWidthCh, getTypingCursor } from './Cbullshitcoding.typing';
import { useCbullshitcodingAnimation } from './useCbullshitcodingAnimation';
import { useCbullshitcodingContent } from './useCbullshitcodingContent';
import { useCbullshitcodingCurrentLine } from './useCbullshitcodingCurrentLine';
import { useCbullshitcodingTypingState } from './useCbullshitcodingTypingState';
import { CbullshitcodingFlow } from './Cbullshitcoding.types';
import type { CbullshitcodingProps } from './Cbullshitcoding.types';

export { colorizeMarkup } from './CbullshitcodingMarkup';
export {
  CbullshitcodingFlow,
  CbullshitcodingTimingEventType,
} from './Cbullshitcoding.types';
export type {
  CbullshitcodingProps,
  CbullshitcodingTimingEvent,
} from './Cbullshitcoding.types';

const DEFAULT_CHAR_TYPE_SPEED_MS = 5;
const DEFAULT_WORD_TYPE_SPEED_MS = 50;
const DEFAULT_PAUSE_BETWEEN_LINES_MS = 50;
const DEFAULT_FADE_DURATION_MS = 300;

export function Cbullshitcoding({
  codeFile,
  strings: propStrings,
  contentKey,
  temper = Temper.Good,
  writingChunks = WritingChunks.Letter,
  speed = 1.0,
  align = TextAlign.Left,
  loop = true,
  clearOnEnd = false,
  style,
  fadeWrapperStyle,
  textSize = 14,
  onCycleComplete,
  charTypeSpeedMs = DEFAULT_CHAR_TYPE_SPEED_MS,
  wordTypeSpeedMs = DEFAULT_WORD_TYPE_SPEED_MS,
  pauseBetweenLinesMs = DEFAULT_PAUSE_BETWEEN_LINES_MS,
  fadeDurationMs = DEFAULT_FADE_DURATION_MS,
  onTimingEvent,
  trailingLineDropCount = 0,
  trailingLineDropPauseMs = DEFAULT_PAUSE_BETWEEN_LINES_MS,
  maxDisplayLineLength,
  trimCommonIndent = false,
  centerBlock = false,
  replacements,
  flow = CbullshitcodingFlow.CenteredSoftEdges,
}: CbullshitcodingProps): ReactElement {
  const typingState = useCbullshitcodingTypingState();
  const { strings, isFadingOut, animationResetKey } = useCbullshitcodingContent({
    codeFile,
    propStrings,
    contentKey,
    fadeDurationMs,
    trailingLineDropCount,
    maxDisplayLineLength,
    trimCommonIndent,
    replacements,
    hasContentRef: typingState.hasContentRef,
    resetTypingState: typingState.resetTypingState,
  });
  const currentLine = useCbullshitcodingCurrentLine({
    strings,
    currentLineIndex: typingState.currentLineIndex,
    currentTypingLine: typingState.currentTypingLine,
  });

  useCbullshitcodingAnimation({
    strings,
    animationResetKey,
    isFadingOut,
    writingChunks,
    speed,
    loop,
    clearOnEnd,
    charTypeSpeedMs,
    wordTypeSpeedMs,
    pauseBetweenLinesMs,
    trailingLineDropCount,
    trailingLineDropPauseMs,
    onCycleComplete,
    onTimingEvent,
    typingState,
  });

  return (
    <CbullshitcodingViewport
      completedLines={typingState.completedLines}
      currentLine={currentLine}
      currentLineCursor={getTypingCursor({ writingChunks })}
      temper={temper}
      textSize={textSize}
      align={align}
      isFadingOut={isFadingOut}
      fadeDurationMs={fadeDurationMs}
      style={style}
      fadeWrapperStyle={fadeWrapperStyle}
      centerBlock={centerBlock}
      centeredBlockWidthCh={getCenteredBlockWidthCh({ centerBlock, lines: strings })}
      flow={flow}
    />
  );
}
